import merge from 'lodash/merge';
import { bigQuery } from '@fiverr-private/obs';
import type { TrackingData } from '../../../shared/lib/getTrackingData';
import {
    EVENT_TYPES,
    EVENTS,
    TRACKING_STATIC_CONTEXT,
    ENRICHABLE_EVENTS,
    SOURCE_TO_TRACKING_PAGE_NAME_MAP,
} from './constants';

interface TrackPageViewConfig {
    route?: string;
    pageElementType?: string;
    httpRequest?: { url: string; referrer: string };
    traffic?: any;
}

export const getBigQueryPageViewEventType = (appName: string, route?: string) => {
    if (!route) {
        return `logo_maker_${appName}_show`;
    }

    return `logo_maker_${appName}_${route}_show`;
};

const prepareBigQueryPayload = (event: any, context: any, payload = {}, withSource = true) => {
    const { refCtxId, source, ...restOfContext } = context;
    const pageReferrer = {
        page: {
            referrer: {
                ctx_id: refCtxId,
                ...(withSource && {
                    name: SOURCE_TO_TRACKING_PAGE_NAME_MAP[source] || source,
                }),
            },
        },
    };

    return merge({}, restOfContext, event, payload, pageReferrer);
};

/**
 * Tracking class util, Used to track client events.
 */
export class Monitor {
    appName = '';
    private _context: any;

    initialize({ appName, pageName, ...trackingContext }: TrackingData) {
        if (!appName) {
            throw Error("Tracker was initialized without appName. Please check your application's server entry.");
        }

        this.appName = appName;
        this._context = {
            ...TRACKING_STATIC_CONTEXT,
            ...trackingContext,
            ...(pageName && { page: { name: pageName } }),
        };
    }

    get context() {
        return this._context || {};
    }

    /**
     * Updates current context dynamically.
     */
    updateContext(update: any) {
        this._context = merge({}, this.context, update);
    }

    /**
     * Tracks a given event into proper platforms
     */
    track(type: string, payload: any) {
        const event = EVENTS[type];

        if (event) {
            const combinedPayload = prepareBigQueryPayload({ type, ...event }, this.context, payload, false);

            bigQuery.send(combinedPayload);
        }
    }

    /**
     * Tracks an array of given events into bigQuery.
     */
    multitrack(events: any[]) {
        events
            .map(({ type, payload }) => {
                const event = EVENTS[type];
                if (event) {
                    return prepareBigQueryPayload({ type, ...event }, this.context, payload, false);
                }
                return null;
            })
            .filter((event) => !!event)
            .forEach((event) => bigQuery.send(event));
    }

    /**
     * Provides an enrichment of current context for events
     * that are created & reported by other services).
     */
    enrich(type: string, platform: string) {
        return platform
            ? {
                  [platform]: {
                      ...this.context,
                      ...ENRICHABLE_EVENTS[type],
                  },
              }
            : {
                  ...this.context,
                  ...ENRICHABLE_EVENTS[type],
              };
    }

    /**
     * Tracks a page view event.
     */
    trackPageView({ route, pageElementType, httpRequest, traffic }: TrackPageViewConfig = {}) {
        const event = {
            type: getBigQueryPageViewEventType(this.appName, route),
            http_request: httpRequest,
            traffic_source: traffic,
            ...(pageElementType && {
                page: { element: { type: pageElementType } },
            }),
            ...EVENTS[EVENT_TYPES.PAGE_VIEW],
        };

        const payload = prepareBigQueryPayload(event, this.context);
        bigQuery.send(payload);
    }
}
